import { forwardRef } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '../../../lib/cn';

/**
 * Single MCQ option button — used by both the onboarding Stage 2.5
 * Recognition card (`word → pick the definition`) and the review-flow
 * MCQ (`definition → pick the word`). Feedback colors are shared so
 * "correct" looks the same green in both flows, "wrong" the same red,
 * and the in-pill check/X icons are sized + positioned consistently.
 *
 * Props:
 *   state — 'default' | 'student_correct' | 'student_wrong'
 *           | 'reveal_correct' | 'unselected'
 *     - default          : no answer yet, hoverable
 *     - student_correct  : student picked this, it's right → green + Check
 *     - student_wrong    : student picked this, it's wrong → red + X
 *     - reveal_correct   : student missed, this was right → green + Check
 *     - unselected       : student picked something else  → faded
 *   layout — 'row' | 'grid'
 *     - row  : letter prefix on the left, text fills, icon on the right
 *              (Stage 2.5 — three definitions stacked vertically)
 *     - grid : centered text, no letter prefix, icon overlaid bottom-right
 *              (Review McqDefinitionToWord — short word choices in a 2x2)
 *   letter — the option letter ('A', 'B', ...) for layout=row; ignored otherwise
 *   text   — visible label
 *   onClick, disabled — standard button props
 */
const STATE_CLASSES = {
  default: {
    border: 'border-gray-200 dark:border-gray-700',
    bg: 'bg-white dark:bg-gray-900',
    text: 'text-gray-700 dark:text-gray-300',
    hover:
      'hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] cursor-pointer',
  },
  student_correct: {
    border: 'border-green-400 dark:border-green-600',
    bg: 'bg-green-50/60 dark:bg-green-900/20',
    text: 'text-gray-900 dark:text-white',
    hover: 'cursor-default',
  },
  student_wrong: {
    border: 'border-red-300 dark:border-red-700',
    bg: 'bg-red-50/60 dark:bg-red-900/20',
    text: 'text-gray-900 dark:text-white',
    hover: 'cursor-default',
  },
  reveal_correct: {
    border: 'border-green-400 dark:border-green-600',
    bg: 'bg-green-50/60 dark:bg-green-900/20',
    text: 'text-gray-900 dark:text-white',
    hover: 'cursor-default',
  },
  unselected: {
    border: 'border-gray-200 dark:border-gray-700',
    bg: 'bg-white dark:bg-gray-900',
    text: 'text-gray-400 dark:text-gray-500',
    hover: 'cursor-default',
  },
};

const MultipleChoiceOption = forwardRef(function MultipleChoiceOption(
  { state = 'default', layout = 'row', letter, text, onClick, disabled, dataTestid, focusKey, className },
  ref
) {
  const classes = STATE_CLASSES[state] || STATE_CLASSES.default;
  const showCheck = state === 'student_correct' || state === 'reveal_correct';
  const showX = state === 'student_wrong';

  if (layout === 'grid') {
    return (
      <button
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        data-testid={dataTestid}
        data-live-focus-key={focusKey}
        className={cn(
          'relative w-full px-4 py-4 rounded-lg border text-center font-medium text-lg transition-colors',
          classes.border,
          classes.bg,
          classes.text,
          classes.hover,
          className
        )}
      >
        {text}
        {(showCheck || showX) && (
          <span className="absolute top-2 right-2">
            {showCheck && (
              <Check
                className="w-4 h-4 text-green-600 dark:text-green-400"
                strokeWidth={2.5}
              />
            )}
            {showX && (
              <X className="w-4 h-4 text-red-500 dark:text-red-400" strokeWidth={2.5} />
            )}
          </span>
        )}
      </button>
    );
  }

  // layout === 'row'
  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestid}
      data-live-focus-key={focusKey}
      className={cn(
        'w-full text-left px-4 py-3 rounded-lg border transition-colors',
        classes.border,
        classes.bg,
        classes.hover,
        className
      )}
    >
      <div className="flex items-start gap-3">
        {letter && (
          <span className="text-sm font-medium text-gray-400 dark:text-gray-500 mt-0.5 w-5 flex-shrink-0">
            {letter}
          </span>
        )}
        <span className={cn('text-base flex-1', classes.text)}>{text}</span>
        {showCheck && (
          <Check
            className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
            strokeWidth={2.5}
          />
        )}
        {showX && (
          <X
            className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5"
            strokeWidth={2.5}
          />
        )}
      </div>
    </button>
  );
});

export default MultipleChoiceOption;
